/**
 * 平台审计日志（平台运营端）
 *
 * 与租户侧的操作日志不同，这里是**跨租户视角**的审计记录，重点盯三类事：
 *   - 跨租户访问（平台运营人员看了哪个租户的什么数据）；
 *   - 越权告警（有人尝试了权限之外的操作）；
 *   - 数据导出（谁把数据导出去了）。
 *
 * 日志只读、不提供删除入口，这是审计的基本要求。
 * 契约中该接口返回的是宽松结构（Record<string, unknown>），
 * 页面做一次归一化，字段缺失时降级为占位符而不是崩溃。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CopyOutlined,
  EyeOutlined,
  FileProtectOutlined,
  ReloadOutlined,
  SafetyOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  AUDIT_CATEGORY_LABELS,
  PermissionCode,
  type AuditCategory,
  type PrincipalType,
} from '@ilc/shared';
import { platformApi } from '@/api/endpoints';
import { mockAuditLogList } from '@/mocks';
import { usePagedList } from '@/hooks/usePagedList';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { feedback } from '@/utils/feedback';

interface Query {
  page?: number;
  pageSize?: number;
  category?: AuditCategory;
  tenantId?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** 归一化后的审计记录行 */
interface AuditRow {
  id: string;
  category: AuditCategory;
  principalType: PrincipalType;
  principalName: string | null;
  tenantId: string | null;
  action: string;
  target: string | null;
  detail: Record<string, unknown> | null;
  ip: string | null;
  traceId: string | null;
  createdAt: string;
}

const CATEGORY_COLORS: Record<AuditCategory, string> = {
  AUTH: 'blue',
  DATA_CHANGE: 'default',
  PROVIDER_CALL: 'cyan',
  CROSS_TENANT_ACCESS: 'purple',
  DATA_EXPORT: 'gold',
  CONFIG_CHANGE: 'geekblue',
  PRIVILEGE_VIOLATION: 'red',
};

const PRINCIPAL_LABELS: Record<PrincipalType, string> = {
  PLATFORM: '平台运营',
  TENANT: '租户用户',
};

/** 需要重点关注的分类（页面上会额外标红） */
const SENSITIVE_CATEGORIES: AuditCategory[] = [
  'CROSS_TENANT_ACCESS',
  'PRIVILEGE_VIOLATION',
  'DATA_EXPORT',
];

function readString(raw: Record<string, unknown>, key: string): string | null {
  const value = raw[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function normalize(raw: Record<string, unknown>, index: number): AuditRow {
  const detail = raw.detail;
  return {
    id: readString(raw, 'id') ?? `audit-fallback-${index}`,
    category: (readString(raw, 'category') as AuditCategory | null) ?? 'DATA_CHANGE',
    principalType: (readString(raw, 'principalType') as PrincipalType | null) ?? 'TENANT',
    principalName: readString(raw, 'principalName'),
    tenantId: readString(raw, 'tenantId'),
    action: readString(raw, 'action') ?? '-',
    target: readString(raw, 'target'),
    detail:
      detail !== null && typeof detail === 'object' && !Array.isArray(detail)
        ? (detail as Record<string, unknown>)
        : null,
    ip: readString(raw, 'ip'),
    traceId: readString(raw, 'traceId'),
    createdAt: readString(raw, 'createdAt') ?? '',
  };
}

function pretty(value: unknown): string {
  if (value === null || value === undefined) return '（无明细）';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export default function AuditLogs(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const [detail, setDetail] = useState<AuditRow | null>(null);

  const list = usePagedList<AuditRow, Query>({
    loader: (q) =>
      platformApi.auditLogs(q).then((page) => ({
        ...page,
        list: page.list.map((row, index) => normalize(row, index)),
      })),
    fallback: (q) => {
      const page = mockAuditLogList(q);
      return {
        ...page,
        list: page.list.map((row, index) => normalize(row as unknown as Record<string, unknown>, index)),
      };
    },
    initialQuery: { page: 1, pageSize: 20 },
  });

  const rows = list.list;
  const stats = useMemo(() => {
    const crossTenant = rows.filter((r) => r.category === 'CROSS_TENANT_ACCESS').length;
    const violation = rows.filter((r) => r.category === 'PRIVILEGE_VIOLATION').length;
    const exportCount = rows.filter((r) => r.category === 'DATA_EXPORT').length;
    return { crossTenant, violation, exportCount };
  }, [rows]);

  return (
    <div className="ilc-page">
      <PageHeader
        title="平台审计日志"
        description="平台运营与租户的关键操作留痕：跨租户访问、权限越界尝试、数据导出、配置变更。用于事后审计与合规备查。"
        tags={<Tag icon={<FileProtectOutlined />} color="purple">只读 · 不可删除</Tag>}
        extra={
          <Button icon={<ReloadOutlined />} onClick={list.reload}>
            刷新
          </Button>
        }
      />

      {!can(PermissionCode.PROVIDER_LOG_READ) ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="当前账号为受限查看"
          description="审计日志仅对平台审计角色开放完整明细。如您只能看到部分记录，请联系平台超级管理员确认角色配置。"
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        icon={<SafetyOutlined />}
        style={{ marginBottom: 12 }}
        message="审计日志为什么不能删"
        description="日志的价值在于「事后还能还原当时发生了什么」。因此平台不提供删除入口，也不做软删除；到期后按保留策略归档到冷存储，归档过程本身同样会被记录。"
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={12} lg={8}>
          <Card size="small">
            <Statistic
              title="跨租户访问（本页）"
              value={stats.crossTenant}
              suffix="次"
              prefix={<EyeOutlined />}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              平台运营人员查看租户数据的行为，需能对应到工单或明确业务原因
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={12} lg={8}>
          <Card size="small">
            <Statistic
              title="越权告警（本页）"
              value={stats.violation}
              suffix="次"
              valueStyle={{ color: stats.violation > 0 ? '#cf1322' : undefined }}
              prefix={stats.violation > 0 ? <WarningOutlined /> : undefined}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              缺少权限的操作尝试会被拒绝并告警，若集中出现说明角色配置可能有问题
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={12} lg={8}>
          <Card size="small">
            <Statistic title="数据导出（本页）" value={stats.exportCount} suffix="次" />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              导出行为会记录导出范围与条数，用于对外提供资料时追溯来源
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <DataTable<AuditRow>
        storageKey="platform-audit-logs"
        exportFileName="平台审计日志"
        exportColumns={[
          { title: '时间', dataIndex: 'createdAt' },
          { title: '分类', value: (r) => AUDIT_CATEGORY_LABELS[r.category] },
          { title: '主体类型', value: (r) => PRINCIPAL_LABELS[r.principalType] },
          { title: '操作人', dataIndex: 'principalName' },
          { title: '动作', dataIndex: 'action' },
          { title: '对象', dataIndex: 'target' },
          { title: '租户', dataIndex: 'tenantId' },
          { title: 'IP', dataIndex: 'ip' },
          { title: 'traceId', dataIndex: 'traceId' },
        ]}
        columns={[
          {
            title: '时间',
            dataIndex: 'createdAt',
            width: 165,
            fixed: 'left',
            sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
            render: (v: string) => (
              <span className="ilc-mono" style={{ fontSize: 12 }}>
                {v ? v.replace('T', ' ').slice(0, 19) : '-'}
              </span>
            ),
          },
          {
            title: '分类',
            dataIndex: 'category',
            width: 130,
            render: (v: AuditCategory) => (
              <Tag
                color={CATEGORY_COLORS[v]}
                icon={SENSITIVE_CATEGORIES.includes(v) ? <WarningOutlined /> : undefined}
              >
                {AUDIT_CATEGORY_LABELS[v]}
              </Tag>
            ),
          },
          {
            title: '操作主体',
            key: 'principal',
            width: 190,
            render: (_v, record) => (
              <Space direction="vertical" size={0}>
                <Space size={4}>
                  <span>{record.principalName ?? '（未记录）'}</span>
                  <Tag color={record.principalType === 'PLATFORM' ? 'purple' : 'default'}>
                    {PRINCIPAL_LABELS[record.principalType]}
                  </Tag>
                </Space>
                {record.tenantId ? (
                  <Typography.Text type="secondary" className="ilc-mono" style={{ fontSize: 11 }}>
                    目标租户：{record.tenantId}
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            title: '动作',
            dataIndex: 'action',
            width: 200,
            render: (v: string) => (
              <span className="ilc-mono" style={{ fontSize: 12 }}>
                {v}
              </span>
            ),
          },
          {
            title: '对象 / 摘要',
            dataIndex: 'target',
            width: 260,
            render: (v: string | null, record) => (
              <Space direction="vertical" size={0}>
                <span>{v ?? '-'}</span>
                {record.detail?.message ? (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {String(record.detail.message)}
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            title: 'IP',
            dataIndex: 'ip',
            width: 130,
            render: (v: string | null) => (
              <span className="ilc-mono" style={{ fontSize: 12 }}>
                {v ?? '-'}
              </span>
            ),
          },
          {
            title: 'traceId',
            dataIndex: 'traceId',
            width: 170,
            render: (v: string | null) =>
              v ? (
                <Typography.Text className="ilc-mono" style={{ fontSize: 12 }} copyable>
                  {v}
                </Typography.Text>
              ) : (
                '-'
              ),
          },
          {
            title: '操作',
            key: 'action-col',
            width: 110,
            fixed: 'right',
            render: (_v, record) => (
              <Button size="small" type="link" icon={<EyeOutlined />} onClick={() => setDetail(record)}>
                明细
              </Button>
            ),
          },
        ]}
        dataSource={rows}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        emptyText="没有匹配的审计记录"
        toolbar={
          <Space wrap>
            <Select
              placeholder="日志分类"
              allowClear
              style={{ width: 160 }}
              value={list.query.category}
              onChange={(value) => list.setQuery({ category: value })}
              options={Object.entries(AUDIT_CATEGORY_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Input.Search
              placeholder="租户 ID"
              allowClear
              style={{ width: 200 }}
              onSearch={(value) => list.setQuery({ tenantId: value })}
            />
            <Select
              placeholder="时间范围"
              allowClear
              style={{ width: 170 }}
              value={
                list.query.dateFrom && list.query.dateTo
                  ? `${list.query.dateFrom}-${list.query.dateTo}`
                  : undefined
              }
              onChange={(value: string | undefined) => {
                if (!value) {
                  list.setQuery({ dateFrom: undefined, dateTo: undefined });
                  return;
                }
                const [from, to] = value.split('-');
                list.setQuery({ dateFrom: from, dateTo: to });
              }}
              options={[
                {
                  value: `${dayjs().format('YYYY-MM-DD')}-${dayjs().format('YYYY-MM-DD')}`,
                  label: '今天',
                },
                {
                  value: `${dayjs().subtract(7, 'day').format('YYYY-MM-DD')}-${dayjs().format('YYYY-MM-DD')}`,
                  label: '近 7 天',
                },
                {
                  value: `${dayjs().subtract(30, 'day').format('YYYY-MM-DD')}-${dayjs().format('YYYY-MM-DD')}`,
                  label: '近 30 天',
                },
              ]}
            />
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      <Drawer
        open={detail !== null}
        title="审计记录明细"
        onClose={() => setDetail(null)}
        width={640}
        extra={
          detail ? (
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                void navigator.clipboard
                  .writeText(
                    [
                      `时间：${detail.createdAt}`,
                      `分类：${AUDIT_CATEGORY_LABELS[detail.category]}`,
                      `操作人：${detail.principalName ?? '-'}（${PRINCIPAL_LABELS[detail.principalType]}）`,
                      `动作：${detail.action}`,
                      `对象：${detail.target ?? '-'}`,
                      `IP：${detail.ip ?? '-'}`,
                      `traceId：${detail.traceId ?? '-'}`,
                      `明细：\n${pretty(detail.detail)}`,
                    ].join('\n'),
                  )
                  .then(() => feedback.success('审计记录已复制'))
                  .catch(() => feedback.warning('复制失败，请手动选中文本复制'));
              }}
            >
              复制记录
            </Button>
          ) : null
        }
      >
        {detail ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {SENSITIVE_CATEGORIES.includes(detail.category) ? (
              <Alert
                type="warning"
                showIcon
                message={`该记录属于需重点关注的分类：${AUDIT_CATEGORY_LABELS[detail.category]}`}
                description={
                  detail.category === 'CROSS_TENANT_ACCESS'
                    ? '平台运营人员跨租户访问数据。请核对是否与工单或明确的业务原因对应，必要时联系操作人确认。'
                    : detail.category === 'PRIVILEGE_VIOLATION'
                      ? '存在权限之外的越权操作尝试。若同一账号反复出现，建议检查角色配置或账号安全。'
                      : '数据导出行为。对外提供资料时应有对应的申请记录，导出范围与条数应与申请一致。'
                }
              />
            ) : null}

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="时间">
                <span className="ilc-mono">{detail.createdAt.replace('T', ' ').slice(0, 19)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="分类">
                <Tag color={CATEGORY_COLORS[detail.category]}>{AUDIT_CATEGORY_LABELS[detail.category]}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="操作主体">
                {detail.principalName ?? '（未记录）'} · {PRINCIPAL_LABELS[detail.principalType]}
              </Descriptions.Item>
              <Descriptions.Item label="目标租户">
                {detail.tenantId ? (
                  <span className="ilc-mono">{detail.tenantId}</span>
                ) : (
                  <Typography.Text type="secondary">（非租户级操作）</Typography.Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="动作">
                <span className="ilc-mono">{detail.action}</span>
              </Descriptions.Item>
              <Descriptions.Item label="对象">{detail.target ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="IP">
                <span className="ilc-mono">{detail.ip ?? '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="traceId">
                <Typography.Text className="ilc-mono" copyable>
                  {detail.traceId ?? '-'}
                </Typography.Text>
              </Descriptions.Item>
            </Descriptions>

            <Card size="small" title="变更 / 访问明细">
              <pre className="ilc-mono ilc-payload">{pretty(detail.detail)}</pre>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                明细为操作当时的原始记录，敏感字段（税号、手机号、银行账号）已脱敏后写入。
              </Typography.Text>
            </Card>

            <Tooltip title="审计日志不可修改、不可删除；如需更正记录内容，请通过补充说明的方式留痕">
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <FileProtectOutlined /> 本记录为只读，只保留策略归档，不提供编辑与删除入口。
              </Typography.Text>
            </Tooltip>
          </Space>
        ) : null}
      </Drawer>
    </div>
  );
}
